import React, { useState, useEffect, useRef, useCallback } from 'react'
import styled from 'styled-components'
import CaretIcon from 'core-system/Icons/Actions/Caret'
import palette from 'core-system/Themes/palette'
import {
  flexbox,
  FlexboxProps,
  space,
  SpaceProps,
  layout,
  LayoutProps,
  PositionProps,
} from 'styled-system'
import pxToRem from 'core-system/utils/pxToRem'
import Text from 'core-system/Text'
import Checkbox from 'core-system/Checkbox'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { SegmentList } from '../../Shared/helperFunctions'
import { uniqBy } from 'lodash-es'

const Container = styled.div<FlexboxProps & SpaceProps & LayoutProps>`
  display: flex;
  flex-direction: column;
  position: relative;
  padding-bottom: 0;
`

const Content = styled.div<{
  open: boolean
  disabled: boolean
}>`
  color: ${(props) => props.theme.palette.text.primary};
  width: 100%;
  display: flex;
  align-items: center;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  width: ${(props) => props.theme.pxToRem(200)};
  min-height: 3.5rem;
  border: 1px solid ${palette.grey.grey300};
  border-radius: ${(props) => (props.open ? '0.5rem 0.5rem 0 0' : '0.5rem')};
  padding: '0.5rem 0.25rem 0.5rem 1rem';
  background-color: white;
  color: ${palette.text.primary};
  &:hover {
    border: ${(props) =>
      props.disabled
        ? `1px solid ${palette.grey.grey300}`
        : `1px solid ${palette.text.secondary}`};
  }

  ${flexbox}
  ${space}
  ${layout}
`

const Placeholder = styled.div`
  ${(props) => props.theme.typography.action4};
  display: flex;
  padding: 0.5rem 0rem;
  align-items: center;
  overflow: hidden;
  margin-left: 1rem;
`

const BubbleText = styled.div<{ showAllSelectedItems: boolean }>`
  display: flex;
  flex-wrap: ${(props) => (props.showAllSelectedItems ? 'wrap' : 'nowrap')};
  overflow: visible;
  text-overflow: ellipsis;
  width: 100%;
`

const Bubble = styled.div<{ showAllSelectedItems: boolean }>`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
  background-color: ${(props) => props.theme.palette.secondary.purple200};
  margin-right: 0.25rem;
  margin-bottom: 0.25rem;
  white-space: ${(props) => (props.showAllSelectedItems ? 'wrap' : 'nowrap')};
`

const CaretWrapper = styled.div<{ open: boolean }>`
  width: ${(props) => props.theme.pxToRem(24)};
  height: ${(props) => props.theme.pxToRem(24)};
  transform: ${(props) => (props.open ? 'rotate(180deg)' : 'rotate(0)')};
  transition: all 0.2s ease-in-out;
  margin-left: auto;
`

const MultiSelectContainer = styled.div<
  FlexboxProps &
    SpaceProps &
    LayoutProps &
    PositionProps & {
      open: boolean
    }
>`
  position: absolute;
  z-index: ${(props) => props.theme.zIndex.dropdowns};
  box-shadow: ${(props) => props.theme.dropShadows.selected};
  border-radius: 0.5rem;
  background-color: ${(props) => props.theme.palette.white};
  width: 100%;
  display: ${(props) => (props.open ? 'block' : 'none')};
  max-height: 30rem;
  overflow-y: auto;
  border: 1px solid ${palette.text.secondary};
  border-radius: 0 0 0.5rem 0.5rem;
  padding-bottom: 0.5rem;

  top: 100%;
  left: 0;

  &::-webkit-scrollbar {
    background-color: transparent;
    width: 0.75rem;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 0.5rem;
    background-color: ${palette.grey.grey300};
    background-clip: content-box;
    border: 3px solid transparent;
  }
`

const ItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  padding-left: 1rem;
  padding-right: 0.5rem;
  margin-bottom: ${pxToRem(3)};
`

const AccordionContainer = styled(Accordion)`
  padding: 0.5rem 0;
  background-color: ${palette.primary.pink50};
  box-shadow: 0 0 0 0 !important;
  margin: 0 !important;

  // disabled styles
  &.Mui-disabled {
    background-color: ${palette.grey.grey100} !important;
    color: ${palette.text.placeholder} !important;
  }
`

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const AccordionDetailsContainer = styled(AccordionDetails)`
  padding: 0 2rem !important;
`

interface NestedAccordionProps {
  items: SegmentList[]
  activeItems: SegmentList[]
  onItemCallback: (activeItems: SegmentList[]) => void
  type: 'CUSTOM' | 'WORKSITE' | 'TAG'
  title: string
  isDisabled?: boolean
}

const NestedAccordion = ({
  items,
  activeItems,
  onItemCallback,
  type,
  title,
  isDisabled = false,
}: NestedAccordionProps) => {
  const segment = items.filter((item) => item.type === type)

  // If there are no segments, return null
  if (segment.length === 0) {
    return null
  }

  const allSegmentsInActiveItems = segment.every((item) =>
    activeItems.some((activeItem) => activeItem.id === item.id)
  )

  // Individual item clicked
  const itemClicked = (item: SegmentList) => {
    // Checking if the item is already in the activeItems array
    if (activeItems.some((activeItem) => activeItem.id === item.id)) {
      // If it is, remove it from the array
      const newActiveItems = activeItems.filter(
        (activeItem) => activeItem.id !== item.id
      )
      onItemCallback(newActiveItems)
    } else {
      // If it is not, add it to the array
      const newActiveItems = uniqBy([...activeItems, item], 'id')
      onItemCallback(newActiveItems)
    }
  }

  // All items clicked
  // If active items has all items in the segment then remove all the items from active items
  // If all the items are not present, add all the items to active items
  const allItemsClicked = () => {
    if (allSegmentsInActiveItems) {
      const newActiveItems = activeItems.filter(
        (activeItem) => !segment.some((item) => item.id === activeItem.id)
      )
      onItemCallback(uniqBy(newActiveItems, 'id'))
    } else {
      onItemCallback(uniqBy([...activeItems, ...segment], 'id'))
    }
  }

  return (
    <AccordionContainer disabled={isDisabled}>
      <AccordionSummary
        expandIcon={
          <KeyboardArrowDownIcon
            sx={{ color: palette.primary.pink800, fontSize: '2rem' }}
          />
        }
      >
        <TitleContainer>
          <Text variant='body1' textColor={palette.text.primary}>
            {title}
          </Text>
          <Checkbox
            checked={allSegmentsInActiveItems}
            onClick={allItemsClicked}
            disabled={isDisabled}
          />
        </TitleContainer>
      </AccordionSummary>
      <AccordionDetailsContainer>
        {segment.map((item) => (
          <ItemContainer key={item.id}>
            <Text variant='body1' textColor={palette.text.primary}>
              {item.text}
            </Text>
            <Checkbox
              checked={activeItems.some(
                (activeItem) => activeItem.id === item.id
              )}
              onClick={() => itemClicked(item)}
              disabled={isDisabled}
            />
          </ItemContainer>
        ))}
      </AccordionDetailsContainer>
    </AccordionContainer>
  )
}

interface DropdownProps
  extends FlexboxProps,
    SpaceProps,
    LayoutProps,
    PositionProps,
    React.HtmlHTMLAttributes<HTMLDivElement> {
  items: SegmentList[]
  activeItems: SegmentList[]
  onItemCallback: (activeItems: SegmentList[]) => void
  showAllSelectedItems?: boolean
  disabled?: boolean
  isCustomGroupDisabled?: boolean
  placeholderText?: string
  selectAllCustomTitle?: string
  selectAllTagsTitle?: string
}

const Dropdown = React.memo((props: DropdownProps) => {
  const {
    items,
    activeItems,
    onItemCallback,
    showAllSelectedItems = true,
    disabled = false,
    isCustomGroupDisabled = false,
    placeholderText = 'Select Groups',
    selectAllCustomTitle = 'Select All Groups',
    selectAllTagsTitle = 'Select All Tags',
  } = props
  const [isOpen, setIsOpen] = useState(false)

  const hasTagItems = items.some((item) => item.type === 'TAG')

  const containerRef = useRef(null)
  const dropdownRef = useRef(null)

  const clickOutside = useCallback(
    (e: MouseEvent) => {
      if (
        containerRef &&
        containerRef.current &&
        containerRef.current.contains(e.target)
      ) {
        return
      }
      setIsOpen(false)
    },
    [containerRef]
  )

  useEffect(() => {
    document.addEventListener('mousedown', clickOutside, false)
    return () => {
      document.removeEventListener('mousedown', clickOutside, false)
    }
  }, [clickOutside])

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) {
        return
      }
      e.stopPropagation()
      setIsOpen(!isOpen)
    },
    [isOpen, disabled]
  )

  const hasActiveItems = activeItems.length > 0

  return (
    <>
      <Container ref={containerRef}>
        <Content
          onClick={handleClick}
          open={isOpen}
          ref={dropdownRef}
          disabled={disabled}
          {...props}
        >
          <Placeholder>
            <BubbleText showAllSelectedItems={showAllSelectedItems}>
              {!hasActiveItems ? (
                <Text variant='action4'>{placeholderText}</Text>
              ) : (
                activeItems.map((activeItem, index) => (
                  <Bubble
                    key={index}
                    showAllSelectedItems={showAllSelectedItems}
                  >
                    {activeItem.text}
                  </Bubble>
                ))
              )}
            </BubbleText>
          </Placeholder>
          <CaretWrapper open={isOpen}>
            <CaretIcon
              color={disabled ? palette.text.placeholder : palette.text.primary}
              height={24}
              width={24}
            />
          </CaretWrapper>
        </Content>
        <MultiSelectContainer open={isOpen} {...props}>
          <div>
            <NestedAccordion
              items={items}
              type='WORKSITE'
              title='Select All Worksites'
              activeItems={activeItems}
              onItemCallback={onItemCallback}
            />
            <NestedAccordion
              items={items}
              type='CUSTOM'
              title={selectAllCustomTitle}
              activeItems={activeItems}
              onItemCallback={onItemCallback}
              isDisabled={isCustomGroupDisabled}
            />
            {hasTagItems && (
              <NestedAccordion
                items={items}
                type='TAG'
                title={selectAllTagsTitle}
                activeItems={activeItems}
                onItemCallback={onItemCallback}
              />
            )}
          </div>
        </MultiSelectContainer>
      </Container>
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  Dropdown.displayName = 'WorksiteGroupDropdown'
}

export default React.memo(Dropdown)
